<script setup>
import i18n from '../i18n';

import { useStore } from 'vuex';
import usePlanPrice from '@/composable/plan-price';
import useAuth from '@/composable/auth';
import { formatNumber } from 'chart.js/helpers';
import { useRoute, useRouter } from 'vue-router';
import useNotifications from '@/composable/notifications';
import { customizeText } from '../helpers/formatter';
import { computed, onMounted, ref } from 'vue';
import useLayouts from '@/composable/layout';
// TODO make the default language comes from the local storage to sve old value
const isDarkMood = ref(false);
const { toggleSidebar, isCollapse } = useLayouts();
const store = useStore();
const getCurrentUser = computed(() => store.getters['auth/getCurrentUser']);

const switchMode = (newMode) => {
  store.dispatch('changeMode', { mode: newMode });
};

function setLanguage(locale) {
  i18n.global.locale = locale;
  localStorage.setItem('lang', locale);
}

function initFullScreen() {
  document.body.classList.toggle('fullscreen-enable');
  if (
    !document.fullscreenElement &&
    /* alternative standard method */
    !document.mozFullScreenElement &&
    !document.webkitFullscreenElement
  ) {
    // current working methods
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    } else if (document.documentElement.mozRequestFullScreen) {
      document.documentElement.mozRequestFullScreen();
    } else if (document.documentElement.webkitRequestFullscreen) {
      document.documentElement.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
    }
  } else {
    if (document.cancelFullScreen) {
      document.cancelFullScreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitCancelFullScreen) {
      document.webkitCancelFullScreen();
    }
  }
}

function toggleDarkMode() {
  const currentTheme = document.documentElement.getAttribute('data-bs-theme');
  const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
  isDarkMood.value = newTheme === 'dark';
  document.documentElement.setAttribute('data-bs-theme', newTheme);
  localStorage.removeItem('theme');
  localStorage.setItem('theme', newTheme);
  let htmlElement = document.querySelector('html');
  htmlElement.classList.remove('light', 'dark');
  htmlElement.classList.add(newTheme);

  switchMode({ mode: newTheme }); // Dispatch the action with the new mode
}

const {
  hasUnreadMsg,
  handleReadMsg,
  handleDeleteAllNotify,
  fetchNotification,
  isNotifyLoading,

  notificationList,
  notifyUnreadCount
} = useNotifications();
const router = useRouter();
const route = useRoute();

const hasPermissions = computed(() => (permission) => {
  return store.getters['auth/hasPermission'](permission);
});
const { LogoutUser } = useAuth();
const { subscriptionRemainingDays } = usePlanPrice();

async function logout() {
  await LogoutUser();
}

async function handleReadMessages() {
  handleReadMsg();
}

async function handleDeleteAll() {
  handleDeleteAllNotify();
}

async function handleRedirection(path, id) {
  handleReadMsg(id);
  router.push(path);
}

async function handleClickNotifications() {
  fetchNotification({ per_page: 5 });
}

onMounted(() => {
  isDarkMood.value = localStorage.getItem('theme') === 'dark';
});
</script>
<template>
  <b-row class="align-items-center" no-gutters style="height: 100% !important">
    <b-col class="d-flex align-items-center" cols="6">
      <el-button class="notifyBtn d-none d-md-inline-block" round text variant="ghost-secondary" @click="toggleSidebar(!isCollapse)">
        <i class="bx bx-menu fs-22"></i>
      </el-button>

      <div class="ms-2">
        <router-link class="logo" to="/dashboard">
          <span class="l">
            <img alt="" height="40" src="../assets/images/logo-light.png" />
          </span>
        </router-link>
      </div>
    </b-col>
    <b-col cols="6">
      <div class="d-flex align-items-center justify-content-end">
        <el-tag v-if="getCurrentUser?.delegatee?.fullname" class="p-1 me-1 d-none d-lg-inline-block" type="warning">
          {{ $t('header.delegatee') }} :
          {{ getCurrentUser?.delegatee?.fullname?.split(' ')[0] }}
        </el-tag>
        <el-tag v-if="getCurrentUser?.delegator?.fullname" class="p-1 d-none d-lg-inline-block" type="success">
          {{ $t('header.delegator') }} :
          {{ getCurrentUser?.delegator?.fullname?.split(' ')[0] }}
        </el-tag>
        <div v-if="hasPermissions('subscription-list-request')" class="ms-1 d-none d-lg-inline-block">
          <el-tag :type="subscriptionRemainingDays > 4 ? 'primary' : 'danger'">
            {{ $t('header.subscription') }}: {{ subscriptionRemainingDays }} {{ $t('header.daysRemaining') }}
          </el-tag>
        </div>

        <el-dropdown trigger="click">
          <el-button
            :disabled="route?.path === '/profile/notifications'"
            class="notifyBtn"
            round
            text
            variant="ghost-secondary"
            @click="handleClickNotifications"
          >
            <el-tooltip
              :content="$t('notifications.unreadNotifications')"
              :disabled="!hasUnreadMsg"
              class="box-item"
              effect="light"
              placement="left"
            >
              <el-badge :hidden="notifyUnreadCount < 1" :max="100" :value="notifyUnreadCount" class="item"
                ><i class="bx bx-bell fs-22"></i
              ></el-badge>
            </el-tooltip>
          </el-button>

          <template #dropdown>
            <div class="dropdown-head rounded-top dropdown-menu-lg">
              <b-row align-v="center" class="m-2" no-gutters>
                <b-col class="mx-1">
                  <h5 style="font-family: 'hkgrotesk', sans-serif">
                    {{ $t('notifications.notification') }}
                    <span v-if="notifyUnreadCount"> ( {{ formatNumber(notifyUnreadCount) }} ) </span>
                  </h5>
                </b-col>
                <b-col class="text-end">
                  <div v-if="!isNotifyLoading && notificationList?.data?.length">
                    <el-tooltip :content="$t('notifications.clearAll')" class="box-item" effect="light" placement="top">
                      <i class="ri-delete-bin-line fs-18 gradient-text align-top me-3" @click="handleDeleteAll"></i>
                    </el-tooltip>
                    <el-tooltip :content="$t('notifications.markAllAsRead')" class="box-item" effect="light" placement="top">
                      <i class="ri-checkbox-circle-line fs-18 gradient-text align-top" @click="handleReadMessages"></i>
                    </el-tooltip>
                  </div>
                </b-col>
              </b-row>
              <b-row no-gutters>
                <b-col v-if="isNotifyLoading" class="d-flex justify-content-center align-items-center" cols="12">
                  <div class="d-flex justify-content-center mb-3">
                    <b-spinner class="d-block mx-auto" label="large Spinning" type="grow" variant="info" />
                  </div>
                </b-col>
                <b-col v-else-if="!notificationList?.data?.length">
                  <div class="d-flex justify-content-center mb-3">
                    <div class="text-h6 text-center full-width m-auto text-muted my-5">
                      {{ $t('notifications.noNotificationFound') }}
                    </div>
                  </div>
                </b-col>
                <b-col v-else cols="12">
                  <!-- Striped Rows -->
                  <table class="table table-hover">
                    <tbody>
                      <tr
                        v-for="notify in notificationList?.data"
                        :class="{ 'gradient-border-color  table-active': !notify.is_read }"
                        style="cursor: pointer"
                        @click="handleRedirection(notify?.data?.path, notify?.id)"
                      >
                        <td>
                          <div class="d-flex align-items-center border-warning">
                            <div class="flex-shrink-0 align-items-center">
                              <img
                                v-if="notify.data?.sender?.image"
                                :alt="notify.data?.sender?.fullname"
                                :src="notify.data?.sender?.image"
                                class="avatar-xs rounded-circle"
                              />
                              <div v-else class="avatar-sm">
                                <div class="avatar-title rounded-circle text-primary bg-light">
                                  {{ notify.data?.sender?.fullname?.charAt(0) || '-' }}
                                </div>
                              </div>
                            </div>
                            <div class="flex-grow-1 ms-3">
                              <h5 class="fs-13 header-font">{{ notify.data.message }}</h5>
                              <div class="d-flex justify-content-between">
                                <p class="text-muted mb-0 text-capitalize" style="font-size: 12px">
                                  {{ notify?.created_at }}
                                </p>
                                <b-badge class="bg-secondary-subtle text-secondary">
                                  {{ customizeText(notify?.data?.group) || '-' }}
                                </b-badge>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                    <!--                    <tfoot>-->
                    <!--                      <tr>-->
                    <!--                        <td class="text-center text-muted">-->
                    <!--                          <i class="ri-flag-line"></i>-->
                    <!--                          <br />-->
                    <!--                          <p>That's all your notifications from the last 30 days.</p>-->
                    <!--                        </td>-->
                    <!--                      </tr>-->
                    <!--                    </tfoot>-->
                  </table>
                </b-col>
                <b-col v-if="notificationList?.data?.length" class="text-center mb-2" cols="12">
                  <router-link :to="{ name: 'notifications' }">
                    <el-button class="gradient-text" type="text"
                      >{{ $t('notifications.viewAllNotifications') }} <i class="bx bx-link-external fs-20 ms-2"></i
                    ></el-button>
                  </router-link>
                </b-col>
              </b-row>
            </div>
          </template>
        </el-dropdown>

        <!--        <el-dropdown trigger="click">-->
        <!--          <el-button class="notifyBtn" round text variant="ghost-secondary">-->
        <!--            <i class="ri-global-line fs-22"></i>-->
        <!--          </el-button>-->

        <!--          <template #dropdown>-->
        <!--            <div style="width: 100%">-->
        <!--              <div class="p-2" style="cursor: pointer" @click="setLanguage('en')">English</div>-->
        <!--              <div class="p-2" style="cursor: pointer" @click="setLanguage('de')">Germany</div>-->
        <!--              <div class="p-2" style="cursor: pointer" @click="setLanguage('kr')">Kurdish</div>-->
        <!--            </div>-->
        <!--          </template>-->
        <!--        </el-dropdown>-->

        <el-button class="notifyBtn m-0 d-none d-md-inline-block" round text variant="ghost-secondary" @click="toggleDarkMode">
          <i :class="!isDarkMood ? 'bx bx-moon fs-22' : 'ri-sun-line fs-22'"></i>
        </el-button>
        <el-button
          class="notifyBtn m-0 d-none d-md-inline-block"
          data-toggle="fullscreen"
          round
          text
          variant="ghost-secondary"
          @click="initFullScreen"
        >
          <i class="bx bx-fullscreen fs-22"></i>
        </el-button>

        <el-dropdown size="large">
          <el-image
            :src="getCurrentUser.image"
            alt="Header Avatar"
            class="rounded-circle"
            style="width: 40px; margin-right: 10px; height: 40px"
          />
          <template #dropdown>
            <div class="pa-3" style="width: 100%">
              <div class="border-bottom pa-4">
                <span class="d-flex justify-content-between align-self-center align-items-center">
                  <el-image :src="getCurrentUser.image" alt="Header Avatar" class="header-profile-user avatar-md" />
                  <span class="text-start ms-xl-2 align-items-center" style="min-width: 200px">
                    <span class="ms-1 d-block fw-medium"> {{ getCurrentUser.fullname }}</span>
                    <span class="ms-1 fs-12 d-block text-muted">{{ getCurrentUser.position }}</span>
                  </span>
                </span>
              </div>

              <div>
                <el-dropdown-menu style="width: 100%">
                  <el-dropdown-item style="width: 100%" @click="router.push({ name: 'profile' })">
                    <div class="my-1">
                      <i class="mdi mdi-account-circle text-muted fs-16 align-middle me-1"></i>
                      <span class="align-middle">{{ $t('header.profile') }}</span>
                    </div>
                  </el-dropdown-item>
                  <el-dropdown-item style="width: 100%" @click="router.push({ name: 'notifications-settings' })">
                    <div class="my-1">
                      <i class="bx bx-bell text-muted fs-16 align-middle me-1"></i>
                      <span class="align-middle">{{ $t('notifications.notificationSetting') }}</span>
                    </div>
                  </el-dropdown-item>

                  <el-dropdown-item style="width: 100%" @click="toggleDarkMode">
                    <div class=" ">
                      <i :class="!isDarkMood ? 'bx bx-moon fs-22' : 'ri-sun-line fs-22'" class="text-muted align-middle me-1 my-1"></i>

                      <span class="align-middle">{{ $t('header.theme') }}</span>
                    </div>
                  </el-dropdown-item>
                  <el-dropdown-item class="d-lg-none d-sm-inline-block" style="width: 100%" @click="initFullScreen">
                    <div data-toggle="fullscreen">
                      <i :class="'bx bx-fullscreen fs-22'" class="text-muted align-middle me-1 my-1"></i>

                      <span class="align-middle">{{ $t('header.theme') }}</span>
                    </div>
                  </el-dropdown-item>
                  <el-dropdown-item style="width: 100%" @click="logout()">
                    <div class=" ">
                      <i class="mdi mdi-logout text-muted fs-16 align-middle me-1 my-1"></i>
                      <span class="align-middle">{{ $t('header.logout') }}</span>
                    </div>
                  </el-dropdown-item>
                  <el-dropdown-item class="d-lg-none d-sm-inline-block" style="width: 100%" @click="router.push({ name: 'subscription' })">
                    <div>
                      <div class="align-items-center flex-column text-center justify-content-center">
                        <el-tag v-if="getCurrentUser?.delegatee?.fullname" class="d-block mb-1" type="warning">
                          {{ $t('header.delegatee') }} :
                          {{ getCurrentUser?.delegatee?.fullname?.split(' ')[0] }}
                        </el-tag>
                        <el-tag v-if="getCurrentUser?.delegator?.fullname" class="d-block mb-1" type="success">
                          {{ $t('header.delegator') }} :
                          {{ getCurrentUser?.delegator?.fullname?.split(' ')[0] }}
                        </el-tag>
                        <el-tag
                          v-if="hasPermissions('subscription-list-request')"
                          :type="subscriptionRemainingDays > 4 ? 'primary' : 'danger'"
                          class="d-block mb-1"
                        >
                          {{ $t('header.subscription') }}: {{ subscriptionRemainingDays }}
                          {{ $t('header.daysRemaining') }}
                        </el-tag>
                      </div>
                    </div>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </div>
            </div>
          </template>
        </el-dropdown>
      </div>
    </b-col>
  </b-row>
</template>
<style>
.gradient-border-color {
  border-left: 2px solid #f79d65;
}

.notifyBtn.el-button:hover {
  background-color: unset !important;
  border-color: unset !important;
  color: unset !important;
  box-shadow: none !important;
}

.header-font {
  font-family: 'hkgrotesk', sans-serif !important;
  font-size: 15px;
}
</style>
